'use client';

import { useEffect, useRef } from 'react';
import Image, { StaticImageData } from '../image';
import { useInView } from '@react-spring/web';
import { create, UseBoundStore, StoreApi } from 'zustand';
import { HelpTitle } from '../help-title';
import { useMedia } from '@ssv-website/hooks';

export interface ISectionHeaderProps {
  title: string;
  icon?: string | StaticImageData;
}

export interface ISectionBodyTitleProp {
  title: string | React.ReactNode;
  subTitle?: React.ReactNode | string;
  count?: number;
  className?: string;
}

export interface ISectionBodyProp extends ISectionBodyTitleProp {
  tip?: string;
  children?: React.ReactNode | string;
  extra?: React.ReactNode | string;
  help?: string;
  pb0?: boolean;
}

export interface ISectionCardProp extends ISectionHeaderProps {
  children?: React.ReactNode;
  id?: string;
  topTip?: string;
  className?: string;
}
interface ITitleCountProps {
  count?: number;
}

interface IInitalState {
  ids: {
    //显示的ID， 是否显示
    [key: string]: boolean;
  };
}

interface IActions {
  cleanId(id: string): void;
  setInView(id: string, isInView: boolean): void;
}

const initalState: IInitalState = {
  ids: {},
};

export const useSectionCardStore: UseBoundStore<
  StoreApi<IInitalState & IActions>
> = create((set) => ({
  ...initalState,
  cleanId(id: string) {
    set((p: IInitalState) => ({
      ...p,
      ids: {
        ...p.ids,
        [id]: false,
      },
    }));
  },
  setInView(id: string, isInView: boolean) {
    set((p: IInitalState) => ({
      ...p,
      ids: {
        ...p.ids,
        [id]: isInView,
      },
    }));
  },
}));

const useSection = () => {
  // const [ref, isInView] = useInView({
  //   amount: 0.5,
  // });
  const ref = useRef<HTMLDivElement>(null);

  const setInView = useSectionCardStore((s) => s.setInView);
  useEffect(() => {
    if (ref.current) {
      setInView(ref.current?.id, false);
    }
  }, []);

  return {
    ref,
  };
};

export const SectionHeader: React.FC<ISectionHeaderProps> = ({
  title,
  icon = '',
}) => {
  const isIconUrl =
    typeof icon === 'string' && (/^(https?:)?\/\//.test(icon) || icon.startsWith('/'));

  return (
    <div className="flex gap-x-[10px]">
      {icon &&
        (isIconUrl ? (
          <img
            src={icon}
            alt="块图标"
            className="w-[50px] h-[50px] mobile:w-[28px] mobile:h-[28px]"
          />
        ) : (
          typeof icon !== 'string' && (
            <Image
              src={icon}
              alt="块图标"
              className="w-[50px] h-[50px] mobile:w-[28px] mobile:h-[28px]"
            />
          )
        ))}
      <h2 className="text-4xl leading-[50px] text-primary mobile:text-lg mobile:leading-[26px] font-500 bg-white">
        {title}
      </h2>
    </div>
  );
};

export const SectionBodyTitle: React.FC<ISectionBodyTitleProp> = ({
  title,
  subTitle,
  count = 0,
}) => {
  const isTypeString = typeof title === 'string';
  return (
    <div
      className={`flex ${
        isTypeString ? 'gap-x-5' : ''
      } items-center mobile:flex-col mobile:items-start`}
    >
      {isTypeString ? (
        <h3 className="text-2xl text-primary leading-[34px] mobile:text-base mobile:leading-none mobile:mb-[6px] font-500">
          {title}
        </h3>
      ) : (
        title
      )}
      {subTitle && (
        <>
          <div
            className={`w-px h-6 bg-primary/[.21] mobile:hidden ${
              isTypeString ? '' : 'ml-[10px] mr-[30px]'
            }`}
          />
          <p className="flex items-center gap-[6px] mobile:gap-1">
            {count > 0 && <TitleCount count={count} />}
            <span className="text-base text-primary/[.6] mobile:text-xs mobile:leading-[18px] line-clamp-1">
              {subTitle}
            </span>
          </p>
        </>
      )}
    </div>
  );
};

const TitleCount: React.FC<ITitleCountProps> = ({ count }) => {
  return (
    <span className="text-[28px] -leading-[1px] bg-clip-text bg-number text-transparent font-tcn mobile:text-xs mobile:leading-[18px] font-500">
      {count}
    </span>
  );
};

export const SectionBody: React.FC<ISectionBodyProp> = ({
  title,
  subTitle,
  tip,
  children,
  extra,
  count,
  help,
  className = 'bg-bg',
  pb0 = false,
}) => {
  const isTypeString = typeof title === 'string';
  const isMobile = useMedia('mobile');
  return (
    <div
      className={`rounded-2xl p-[30px] ${
        isTypeString ? '' : 'pt-5'
      } mobile:p-5 mobile:rounded-lg ${pb0 ? 'pb-0 mobile:pb-0' : ''} ${
        className || 'bg-bg'
      }`}
    >
      <div
        className={`flex items-center justify-between mobile:mb-6 ${
          isTypeString ? 'mb-[30px]' : 'mb-5'
        }`}
      >
        <SectionBodyTitle title={title} subTitle={subTitle} count={count} />
        {extra ? <div className="shrink-0">{extra}</div> : null}
        {tip && !isMobile && <HelpTitle title={tip} help={help} />}
      </div>
      {children}
    </div>
  );
};

export const SectionCard: React.FC<ISectionCardProp> = ({
  id,
  title,
  icon,
  children,
  topTip,
  className,
}) => {
  const { ref } = useSection();
  return (
    <div
      ref={ref}
      className={`flex flex-col gap-y-10 max-w-5xl w-full pt-20 scroll-m-[92px] mobile:w-full mobile:px-5 mobile:pt-10 mobile:gap-y-5 mobile:mt-0 desktop:px-5 ${className}`}
      id={id}
    >
      <div className="flex flex-col gap-y-[10px]">
        {topTip && (
          <span className="text-sm text-primary leading-none">{topTip}</span>
        )}
        <SectionHeader title={title} icon={icon} />
      </div>
      <div className="flex flex-col gap-y-10 mobile:gap-y-5">{children}</div>
    </div>
  );
};
